import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    ArrowSquareOut,
    BookOpen,
    Clock,
    Lightning,
    Package,
    PencilSimple,
    Route,
    Tag,
    Users,
    Warning,
} from 'shared/icons';
import CampaignItemCompetitors from '../components/campaigns/CampaignItemCompetitors';
import CampaignItemFamily from '../components/campaigns/CampaignItemFamily';
import CampaignItemPromotions from '../components/campaigns/CampaignItemPromotions';
import CampaignItemTimeline from '../components/campaigns/CampaignItemTimeline';
import CampaignItemTracking from '../components/campaigns/CampaignItemTracking';
import {
    ATTENTION_LABELS,
    BUYBOX_BADGES,
    ComputeItemFees,
    DASH,
    IsNum,
    ML_STATUS_BADGES,
    Pill,
} from '../components/campaigns/campaignCardShared';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { DescribeListingType, DescribeLogisticType, DescribeShippingMode } from '../constants/mlAnuncio';
import api from '../services/api';
import { FormatDateTimeBr } from '../utils/date';
import { FormatDecimal1, FormatInt, FormatMoney } from '../utils/format';
import { MLB_URLS } from '../utils/ml';

/**
 * PÁGINA DO ANÚNCIO — tudo o que o sistema sabe sobre UM anúncio do ML, num
 * lugar só: preços e campanha ativa, melhor campanha, tarifas reais, estoque,
 * vendas, catálogo, família (lista × cópia de catálogo), o acompanhamento de
 * TODAS as campanhas (com candidaturas) e a linha do tempo da campanha ATIVA
 * × preço. É o destino de clicar no produto no monitor e da notificação.
 *
 * Os números vêm do MESMO endpoint que alimenta a linha do monitor
 * (`GET /campaigns/items/:itemId`), então card e página nunca discordam.
 * Só leitura: os botões "Editar"/"Campanhas" abrem o Mercado Livre — o
 * sistema não escreve no ML.
 */

const TABS = [
    { id: 'campanhas', label: 'Campanhas', icon: Tag },
    { id: 'acompanhamento', label: 'Acompanhamento', icon: Clock },
    { id: 'linha-do-tempo', label: 'Linha do tempo', icon: Route },
    { id: 'familia', label: 'Família', icon: Package },
    { id: 'concorrencia', label: 'Concorrência', icon: Users },
];
const TAB_IDS = new Set(TABS.map((tab) => tab.id));
const TAB_ALIASES = { historico: 'acompanhamento', precos: 'linha-do-tempo', timeline: 'linha-do-tempo' };

const ACTION_CLASS =
    'inline-flex items-center gap-1.5 rounded-lg border border-mono-300 bg-white px-3 py-1.5 text-sm font-medium text-mono-800 transition-colors hover:border-mono-500 hover:text-black dark:border-mono-700 dark:bg-mono-950 dark:text-mono-200 dark:hover:border-mono-500 dark:hover:text-white';

const money = (value) => (IsNum(value) ? FormatMoney(Number(value)) : DASH);
const pct = (value) => (IsNum(value) ? `${FormatDecimal1(Number(value))}%` : DASH);

/**
 * Chip de metadado do anúncio: "rótulo valor" numa pílula.
 *
 * A linha era texto corrido com os CÓDIGOS da API ("Categoria MLB437616 · Tipo
 * gold_special · Envio me2"). O código não sumiu, mudou de lugar: vai no
 * `title`, porque é ele que casa com o payload do ML na hora de conferir.
 * Valor ausente = chip ausente, nada de "Categoria —".
 */
const MetaChip = ({ label, value, code = null, title = null, muted = false }) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const mostraCodigo = code && String(code) !== String(value);
    const hint = [title, mostraCodigo ? `Código do ML: ${code}` : null].filter(Boolean).join(' · ') || undefined;
    return (
        <span
            className={`inline-flex max-w-full items-center gap-1 rounded-full border border-mono-200 px-2 py-0.5 text-[11px] dark:border-mono-800 ${
                muted ? 'bg-mono-50 dark:bg-mono-900/40' : 'bg-white dark:bg-mono-950'
            }`}
            title={hint}
        >
            <span className="shrink-0 text-mono-500 dark:text-mono-400">{label}</span>
            <span className="truncate font-semibold text-mono-800 dark:text-mono-200">{value}</span>
        </span>
    );
};

/** Cartão de indicador: rótulo pequeno, valor grande, linha de apoio opcional. */
const Kpi = ({ label, value, sub = null, tone = 'text-black dark:text-white', title }) => (
    <div
        className="rounded-xl border border-mono-200 bg-white px-4 py-3 dark:border-mono-800 dark:bg-mono-950"
        title={title}
    >
        <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">{label}</p>
        <p className={`mt-1 truncate text-lg font-bold tabular-nums ${tone}`}>{value}</p>
        {sub ? <p className="mt-0.5 truncate text-xs text-mono-500 dark:text-mono-400">{sub}</p> : null}
    </div>
);

const CampaignItemPage = () => {
    const { itemId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Aba pela URL (?tab=), para a notificação cair direto no histórico e o
    // link ser compartilhável. Sem `tab` válido, começa em Campanhas. Nomes
    // antigos continuam valendo: `historico` (sino de notificações) é o
    // acompanhamento por campanha; `precos`/`timeline` é a linha do tempo.
    const rawTab = new URLSearchParams(location.search).get('tab');
    const requestedTab = TAB_ALIASES[rawTab] || rawTab;
    const [activeTab, setActiveTab] = useState(TAB_IDS.has(requestedTab) ? requestedTab : 'campanhas');
    useEffect(() => {
        if (TAB_IDS.has(requestedTab)) setActiveTab(requestedTab);
    }, [requestedTab]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getMonitorItem(itemId)
            .then((res) => {
                if (!cancelled) setItem(res?.item || null);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.message || 'Não foi possível carregar o anúncio.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [itemId]);

    const campaigns = useMemo(() => (Array.isArray(item?.campaigns) ? item.campaigns : []), [item]);
    const fees = useMemo(() => (item ? ComputeItemFees(item) : null), [item]);

    const loadedItemId = item?.item_id;
    useEffect(() => {
        if (!loadedItemId || activeTab !== 'campanhas') {
            return;
        }
        api.markItemCampaignsSeen(loadedItemId).catch(() => {});
    }, [loadedItemId, activeTab]);

    if (loading) return <LoadingSpinner className="py-16" />;
    if (error || !item) {
        return (
            <div className="space-y-4">
                <Link
                    to="/campaigns"
                    className="inline-flex items-center gap-1 text-sm text-info-600 hover:underline dark:text-info-400"
                >
                    <ArrowLeft size={16} /> Monitor Unificado
                </Link>
                <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-800 dark:border-danger-900 dark:bg-danger-950 dark:text-danger-200">
                    {error || `Anúncio ${itemId} não encontrado.`}
                </div>
            </div>
        );
    }

    const isCatalog = item.catalog_listing === true;
    const status = ML_STATUS_BADGES[item.ml_status] || { label: item.ml_status || 'Desconhecido', tone: 'mono' };
    const buybox = BUYBOX_BADGES[item.buybox_status] || null;
    const attentionLabel = item.attention_status
        ? ATTENTION_LABELS[item.attention_status] || item.attention_status
        : null;
    const hasCampaign = Boolean(item.campaign_id || item.campaign_name);
    // Código da API → nome que o vendedor lê no próprio ML. Código desconhecido
    // volta como veio (constants/mlAnuncio nunca inventa rótulo).
    const tipoAnuncio = DescribeListingType(item.listing_type_id);
    const modoEnvio = DescribeShippingMode(item.shipping_mode);
    const logistica = DescribeLogisticType(item.logistic_type);
    // A linha do monitor expõe `price_base` (preço de lista) e `price_consumer`
    // (o que o comprador paga, já com a campanha); `price` cru não vem no wire.
    const consumer = IsNum(item.price_consumer_final)
        ? Number(item.price_consumer_final)
        : IsNum(item.price_consumer)
          ? Number(item.price_consumer)
          : IsNum(item.price_base)
            ? Number(item.price_base)
            : null;
    // "Você recebe" estimado: líquido da campanha (comprador + rebate) menos
    // tarifa e frete reais. Só quando os dois lados existem — nada inventado.
    const recebe =
        IsNum(item.price_net) && fees && IsNum(fees.feeTotal) ? Number(item.price_net) - Number(fees.feeTotal) : null;
    const visibleTabs = TABS.filter((tab) => {
        if (tab.id === 'familia') return Boolean(item.related_item_id);
        if (tab.id === 'concorrencia') return isCatalog;
        return true;
    });
    const currentTab = visibleTabs.some((tab) => tab.id === activeTab) ? activeTab : 'campanhas';

    const selectTab = (id) => {
        setActiveTab(id);
        const params = new URLSearchParams(location.search);
        params.set('tab', id);
        navigate({ search: `?${params.toString()}` }, { replace: true });
    };

    return (
        <div className="space-y-5">
            <Link
                to="/campaigns"
                className="inline-flex items-center gap-1 text-sm text-info-600 hover:underline dark:text-info-400"
            >
                <ArrowLeft size={16} /> Monitor Unificado
            </Link>

            {/* CABEÇALHO: foto, título, selos e os botões para o ML */}
            <header className="flex flex-col gap-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950 md:flex-row md:items-start">
                <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900">
                    {item.thumbnail ? (
                        <img src={item.thumbnail} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <Package size={36} className="text-mono-300 dark:text-mono-700" />
                    )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                    <h1 className="text-xl font-bold text-black dark:text-white md:text-2xl">{item.title || itemId}</h1>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <a
                            href={MLB_URLS.search(item.item_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-sm text-danger-600 hover:underline dark:text-danger-400"
                        >
                            {item.item_id}
                        </a>
                        {item.sku ? (
                            <span className="rounded-md bg-mono-100 px-1.5 py-px font-mono text-xs text-mono-700 dark:bg-mono-900 dark:text-mono-300">
                                SKU {item.sku}
                            </span>
                        ) : null}
                        <Pill tone={status.tone} className="uppercase">
                            {status.label}
                        </Pill>
                        {isCatalog ? (
                            <Pill tone="info" className="uppercase">
                                catálogo
                            </Pill>
                        ) : null}
                        {item.related_item_id ? (
                            <button
                                type="button"
                                onClick={() => selectTab('familia')}
                                className="rounded-md border border-info-200 bg-info-soft px-1.5 py-px font-mono text-xs text-info-700 hover:border-info-400 dark:border-info-900 dark:bg-info-900/30 dark:text-info-300"
                                title="Anúncio sincronizado (lista geral × cópia de catálogo). Clique para comparar."
                            >
                                {isCatalog ? 'lista' : 'catálogo'} {item.related_item_id}
                            </button>
                        ) : null}
                        {attentionLabel ? (
                            <Pill tone="danger" icon={Warning}>
                                {attentionLabel}
                            </Pill>
                        ) : null}
                        {item.promotions_sync_error ? (
                            <Pill
                                tone="danger"
                                className="uppercase"
                                title={`O ML recusou a última consulta (${item.promotions_sync_error})`}
                            >
                                desatualizado
                            </Pill>
                        ) : null}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <MetaChip label="Marca" value={item.brand} />
                        <MetaChip
                            label="Categoria"
                            value={item.category_name || item.category_id}
                            code={item.category_id}
                        />
                        <MetaChip
                            label="Tipo"
                            value={tipoAnuncio?.label}
                            code={tipoAnuncio?.code}
                            title={tipoAnuncio?.hint}
                        />
                        <MetaChip
                            label="Envio"
                            value={modoEnvio?.label}
                            code={modoEnvio?.code}
                            title={modoEnvio?.hint}
                        />
                        <MetaChip
                            label="Logística"
                            value={logistica?.label}
                            code={logistica?.code}
                            title={
                                item.logistic_type === 'fulfillment'
                                    ? 'Estoque no Full: o produto sai de um centro de distribuição do Mercado Livre.'
                                    : 'De onde a mercadoria sai quando vende.'
                            }
                        />
                        <MetaChip
                            label="Campanhas lidas"
                            value={item.promotions_synced_at ? FormatDateTimeBr(item.promotions_synced_at) : null}
                            muted
                        />
                    </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 md:max-w-[320px] md:justify-end">
                    <a
                        href={MLB_URLS.edit(item.item_id)}
                        target="_blank"
                        rel="noreferrer"
                        className={ACTION_CLASS}
                        title="Abrir o anúncio para edição no Seller Center"
                    >
                        <PencilSimple size={16} /> Editar no ML
                    </a>
                    <a
                        href={MLB_URLS.promos(item.item_id)}
                        target="_blank"
                        rel="noreferrer"
                        className={ACTION_CLASS}
                        title="Central de promoções deste anúncio no ML"
                    >
                        <Lightning size={16} /> Campanhas no ML
                    </a>
                    {item.permalink ? (
                        <a
                            href={item.permalink}
                            target="_blank"
                            rel="noreferrer"
                            className={ACTION_CLASS}
                            title="Página pública do anúncio"
                        >
                            <ArrowSquareOut size={16} /> Ver anúncio
                        </a>
                    ) : null}
                    {isCatalog ? (
                        <a
                            href={MLB_URLS.competition(item.item_id)}
                            target="_blank"
                            rel="noreferrer"
                            className={ACTION_CLASS}
                            title="Competição de catálogo no ML"
                        >
                            <BookOpen size={16} /> Catálogo no ML
                        </a>
                    ) : null}
                </div>
            </header>

            {/* INDICADORES — mesma conta da linha do monitor */}
            <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Kpi
                    label="Preço de lista"
                    value={money(item.price_base)}
                    sub={
                        IsNum(item.price_base) && consumer !== null && Number(item.price_base) > consumer
                            ? `desconto de ${pct((1 - consumer / Number(item.price_base)) * 100)} na campanha`
                            : null
                    }
                />
                <Kpi
                    label="Comprador paga"
                    value={money(consumer)}
                    sub={
                        IsNum(item.sale_price_amount)
                            ? `vigente no ML ${money(item.sale_price_amount)}`
                            : 'sem sondagem de preço vigente'
                    }
                    tone={hasCampaign ? 'text-success-700 dark:text-success-400' : 'text-black dark:text-white'}
                />
                <Kpi
                    label="Campanha ativa"
                    value={hasCampaign ? item.campaign_name || item.campaign_id : 'Nenhuma'}
                    sub={
                        hasCampaign
                            ? `${item.campaign_type || ''} · rebate ${pct(item.rebate_percent)} (${money(item.rebate_amount)})${item.current_source === 'sale_price' ? '' : ' · deduzida'}`
                            : item.campaigns_available_count > 0
                              ? `${item.campaigns_available_count} elegíveis`
                              : null
                    }
                    title={
                        hasCampaign
                            ? `Fonte: ${item.current_source === 'sale_price' ? 'origem do preço vigente (ML)' : 'heurística'}`
                            : undefined
                    }
                />
                <Kpi
                    label="Você recebe (estim.)"
                    value={money(recebe)}
                    sub={
                        fees && IsNum(fees.feeTotal)
                            ? `líquido ${money(item.price_net)} − tarifa+frete ${money(fees.feeTotal)}`
                            : 'tarifas ainda não sincronizadas'
                    }
                    title="Preço ao comprador + rebate do ML − tarifa − frete. Estimativa com as tarifas reais gravadas."
                />
                <Kpi
                    label="Estoque"
                    value={FormatInt(item.stock_ml_available ?? 0)}
                    sub={`ML disponível · Bling ${FormatInt(item.stock_bling ?? 0)}${item.stock_ml_unavailable ? ` · indisponível ${FormatInt(item.stock_ml_unavailable)}` : ''}`}
                    tone={
                        IsNum(item.stock_bling) && Number(item.stock_bling) < 10
                            ? 'text-warning-700 dark:text-warning-400'
                            : 'text-black dark:text-white'
                    }
                />
                <Kpi
                    label="Vendas 7 dias"
                    value={FormatInt(item.sales_7d_total ?? 0)}
                    sub={`24h ${FormatInt(item.sales_24h ?? 0)} · média ${FormatDecimal1(item.sales_7d_avg ?? 0)}/dia${IsNum(item.sales_trend_percent) ? ` · ${Number(item.sales_trend_percent) >= 0 ? '+' : ''}${FormatDecimal1(item.sales_trend_percent)}% vs 7 ant.` : ''}`}
                />
            </section>

            {/* Segunda linha: melhor campanha + catálogo + tarifas */}
            <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        Sugestão
                    </p>
                    {item.best_campaign ? (
                        <>
                            <p className="mt-1 text-sm font-semibold text-black dark:text-white">
                                {hasCampaign ? 'Melhor: ' : 'Entrar em: '}
                                {item.best_campaign.campaign_name || item.best_campaign.campaign_id}
                            </p>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                Ganho no líquido{' '}
                                {IsNum(item.best_campaign.net_gain) ? money(item.best_campaign.net_gain) : DASH} por
                                venda. Passa no Pareto: não piora o líquido nem o preço ao comprador.
                            </p>
                        </>
                    ) : item.is_on_best_campaign ? (
                        <p className="mt-1 text-sm font-semibold text-success-700 dark:text-success-400">
                            Já está na melhor campanha
                        </p>
                    ) : (
                        <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">
                            Nenhuma campanha elegível supera a situação atual.
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => selectTab('campanhas')}
                        className="mt-2 text-xs font-semibold text-info-600 hover:underline dark:text-info-400"
                    >
                        Ver as {FormatInt(item.campaigns_available_count ?? campaigns.length)} campanhas elegíveis
                    </button>
                </div>
                <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        Catálogo
                    </p>
                    {isCatalog ? (
                        <>
                            <div className="mt-1 flex items-center gap-2">
                                {buybox ? (
                                    <Pill
                                        tone={item.buybox_status === 'winning' ? 'success' : 'neutral'}
                                        className="uppercase"
                                    >
                                        {buybox.label}
                                    </Pill>
                                ) : (
                                    <span className="text-sm text-mono-500">{DASH}</span>
                                )}
                                {IsNum(item.competitors_count) && Number(item.competitors_count) > 0 ? (
                                    <span className="text-xs text-mono-500 dark:text-mono-400">
                                        {FormatInt(item.competitors_count)} concorrentes
                                    </span>
                                ) : null}
                            </div>
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                {IsNum(item.price_to_win)
                                    ? `Preço para ganhar ${money(item.price_to_win)}`
                                    : 'Sem preço para ganhar informado'}
                                {item.winner_nickname
                                    ? ` · líder ${item.winner_nickname}${IsNum(item.winner_price) ? ` a ${money(item.winner_price)}` : ''}`
                                    : ''}
                            </p>
                            <button
                                type="button"
                                onClick={() => selectTab('concorrencia')}
                                className="mt-2 text-xs font-semibold text-info-600 hover:underline dark:text-info-400"
                            >
                                Ver concorrência
                            </button>
                        </>
                    ) : (
                        <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">
                            Anúncio de lista geral, fora da disputa de catálogo.
                        </p>
                    )}
                </div>
                <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        Tarifas reais
                    </p>
                    {fees && !fees.feesPending ? (
                        <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                            <dt className="text-mono-500 dark:text-mono-400">Tarifa ML</dt>
                            <dd className="text-right font-mono tabular-nums">{money(fees.feeMlDisplay)}</dd>
                            <dt className="text-mono-500 dark:text-mono-400">Fixa</dt>
                            <dd className="text-right font-mono tabular-nums">{money(fees.feeFixed)}</dd>
                            <dt className="text-mono-500 dark:text-mono-400">Frete</dt>
                            <dd className="text-right font-mono tabular-nums">{money(fees.shippingCost)}</dd>
                            <dt className="font-semibold text-black dark:text-white">Total</dt>
                            <dd className="text-right font-mono font-semibold tabular-nums text-black dark:text-white">
                                {money(fees.feeTotal)} {IsNum(fees.feeTotalPct) ? `(${pct(fees.feeTotalPct)})` : ''}
                            </dd>
                        </dl>
                    ) : (
                        <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">
                            Tarifas ainda não sincronizadas para este anúncio.
                        </p>
                    )}
                </div>
            </section>

            {/* ABAS */}
            <section className="space-y-3">
                <div className="flex flex-wrap gap-2 border-b border-mono-200 dark:border-mono-800">
                    {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        const on = tab.id === currentTab;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => selectTab(tab.id)}
                                aria-current={on ? 'true' : undefined}
                                className={`flex items-center gap-2 border-b-2 px-2 py-2.5 text-sm font-medium transition-colors ${
                                    on
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:text-mono-800 dark:hover:text-mono-200'
                                }`}
                            >
                                <Icon size={16} /> {tab.label}
                            </button>
                        );
                    })}
                </div>
                {/* A linha do tempo desenha o PRÓPRIO card (padding 20/24/24 e raio
                    10px da spec, um passo mais claro que a página); dentro do
                    wrapper genérico viraria card dentro de card, com duas bordas
                    concêntricas. As outras abas são conteúdo cru e ganham o wrapper. */}
                {currentTab === 'linha-do-tempo' ? (
                    <CampaignItemTimeline key={item.item_id} itemId={item.item_id} />
                ) : (
                    <div className="rounded-xl border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                        {currentTab === 'campanhas' ? <CampaignItemPromotions campaigns={campaigns} /> : null}
                        {currentTab === 'acompanhamento' ? <CampaignItemTracking itemId={item.item_id} /> : null}
                        {currentTab === 'familia' ? <CampaignItemFamily itemId={item.item_id} /> : null}
                        {currentTab === 'concorrencia' ? <CampaignItemCompetitors itemId={item.item_id} /> : null}
                    </div>
                )}
            </section>
        </div>
    );
};

export default CampaignItemPage;
